/**
 * Owns the state + actions for MPV's "order imagery" flow: the modal-open
 * flag, the currently-selected scene, and the imagery-subset state. Plus
 * the open/close actions that wrap them. Extracted from MPV's
 * component body so the order-imagery concern lives in one place.
 */

import * as I from 'immutable';
import React from 'react';

import {DEFAULT_ORDER_IMAGERY_STATE, OrderImageryState} from 'app/components/OrderImagerySidebar';
import {ApiOrderableScene} from 'app/modules/Remote/Feature';

interface Args {
  /** Used to reset subset ordering when the sidebar collapses. */
  isSidebarExpanded: boolean;
}

export interface OrderImageryFlow {
  isOrderImageryModalOpen: boolean;
  sceneToOrder: I.ImmutableOf<ApiOrderableScene> | null;
  setSceneToOrder: (s: I.ImmutableOf<ApiOrderableScene> | null) => void;
  orderImageryState: OrderImageryState;
  setOrderImageryState: React.Dispatch<React.SetStateAction<OrderImageryState>>;
  openOrderImageryModal: (d: I.ImmutableOf<ApiOrderableScene>) => void;
  closeOrderImageryModal: () => void;
  /**
   * The sidebar's listed, orderable scenes, republished as they stream in or filters change; the
   * modal's Prev/Next step through them.
   */
  previewScenes: readonly I.ImmutableOf<ApiOrderableScene>[];
  setPreviewScenes: (scenes: readonly I.ImmutableOf<ApiOrderableScene>[]) => void;
}

export default function useOrderImageryFlow({isSidebarExpanded}: Args): OrderImageryFlow {
  const [isOrderImageryModalOpen, setIsOrderImageryModalOpen] = React.useState<boolean>(false);
  const [sceneToOrder, setSceneToOrder] = React.useState<I.ImmutableOf<ApiOrderableScene> | null>(
    null
  );
  const [orderImageryState, setOrderImageryState] = React.useState<OrderImageryState>(
    DEFAULT_ORDER_IMAGERY_STATE
  );
  const [previewScenes, setPreviewScenes] = React.useState<
    readonly I.ImmutableOf<ApiOrderableScene>[]
  >([]);

  // Reset the subset draw when the sidebar collapses — there's no UI to
  // clear it once the panel is gone.
  React.useEffect(() => {
    if (!isSidebarExpanded) {
      setOrderImageryState((prev) => ({
        ...prev,
        orderSubset: false,
        subsetFeature: null,
        isAddingSubsetRectangle: false,
      }));
    }
  }, [isSidebarExpanded]);

  function openOrderImageryModal(d: I.ImmutableOf<ApiOrderableScene>) {
    if (!isOrderImageryModalOpen || sceneToOrder !== d) {
      setIsOrderImageryModalOpen(true);
      setSceneToOrder(d);
    }
  }

  function closeOrderImageryModal() {
    setIsOrderImageryModalOpen(false);
    setSceneToOrder(null);
  }

  return {
    isOrderImageryModalOpen,
    sceneToOrder,
    setSceneToOrder,
    orderImageryState,
    setOrderImageryState,
    openOrderImageryModal,
    closeOrderImageryModal,
    previewScenes,
    setPreviewScenes,
  };
}
